import React, { useState } from 'react';

export default function RecurringTaskModal({ isOpen, onClose, onSave, task, listId, listName }) {
  const [recurringFormData, setRecurringFormData] = useState({
    frequency: 'daily', // 'daily', 'weekly', 'monthly', 'interval'
    days: [], // For weekly: [0-6] (Sunday-Saturday), For monthly: [1-31]
    businessDaysOnly: false,
    alsoAddToSpecialList: 'none', // 'none', 'main', 'tomorrow'
    dayInterval: null, // For interval: number of days between recurrences
    weekendHandling: 'after' // 'after' (Monday) or 'before' (Friday) for weekend occurrences
  });

  const handleToggleRecurringDay = (day) => {
    const currentDays = recurringFormData.days || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort((a, b) => a - b);
    
    setRecurringFormData({
      ...recurringFormData,
      days: newDays
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!listId) {
      return;
    }

    // Validate days selection for weekly/monthly
    if (recurringFormData.frequency === 'weekly' && recurringFormData.days.length === 0) {
      alert('Please select at least one day for weekly recurrence.');
      return;
    }

    if (recurringFormData.frequency === 'monthly' && recurringFormData.days.length === 0) {
      alert('Please select at least one day of the month for monthly recurrence.');
      return;
    }

    // Validate day interval for interval frequency
    if (recurringFormData.frequency === 'interval' && (!recurringFormData.dayInterval || recurringFormData.dayInterval < 1)) {
      alert('Please enter a valid number of days (at least 1) for interval recurrence.');
      return;
    }

    // Auto-generate job name as "List Name + Task Name"
    const jobName = `${listName} - ${task.text}`;

    // The API will get the task's text, sub-tasks, and notes automatically
    await onSave({
      task_name: jobName,
      target_list_id: listId,
      frequency: recurringFormData.frequency,
      days: [...recurringFormData.days],
      business_days_only: recurringFormData.businessDaysOnly,
      also_add_to_special_list: recurringFormData.alsoAddToSpecialList,
      day_interval: recurringFormData.dayInterval,
      weekend_handling: recurringFormData.weekendHandling
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Make This Task Recurring</h3>
          <button
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body" style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <div style={{ marginBottom: '1.5rem', padding: '0.75rem', background: 'var(--bg-hover)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-light)' }}>
              <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9375rem', lineHeight: '1.5' }}>
                {task.text}
              </p>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Frequency</label>
              <select
                value={recurringFormData.frequency}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, frequency: e.target.value, days: [], dayInterval: null })}
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="Frequency"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="interval">Every N Days</option>
              </select>
            </div>

            {recurringFormData.frequency === 'interval' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Repeat Every (Days)</label>
                <input
                  type="number"
                  min="1"
                  value={recurringFormData.dayInterval || ''}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, dayInterval: parseInt(e.target.value) || null })}
                  placeholder="e.g., 3 for every 3 days"
                  className="input-custom"
                  style={{ width: '100%' }}
                  required
                  aria-label="Day interval"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                  Number of days between task recurrences
                </p>
              </div>
            )}

            {recurringFormData.frequency === 'weekly' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Days of Week</label>
                <div className="days-selector">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`day-button ${recurringFormData.days.includes(index) ? 'day-button-selected' : ''}`}
                      onClick={() => handleToggleRecurringDay(index)}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {recurringFormData.frequency === 'monthly' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Day(s) of Month</label>
                <div className="days-selector">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <button
                      key={day}
                      type="button"
                      className={`day-button ${recurringFormData.days.includes(day) ? 'day-button-selected' : ''}`}
                      onClick={() => handleToggleRecurringDay(day)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label-checkbox">
                <input
                  type="checkbox"
                  checked={recurringFormData.businessDaysOnly}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, businessDaysOnly: e.target.checked })}
                  style={{ marginRight: '0.5rem' }}
                />
                Only add on business days (skip weekends)
              </label>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                If the scheduled date falls on a weekend, tasks will be moved to a business day
              </p>
            </div>

            {recurringFormData.businessDaysOnly && (
              <div style={{ marginBottom: '1rem', marginLeft: '1.5rem' }}>
                <label className="form-label">Weekend Handling</label>
                <select
                  value={recurringFormData.weekendHandling}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, weekendHandling: e.target.value })}
                  className="input-custom"
                  style={{ width: '100%' }}
                  aria-label="Weekend handling"
                >
                  <option value="after">Add on next business day (Monday)</option>
                  <option value="before">Add on previous business day (Friday)</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                  Choose whether to add weekend tasks before or after the weekend
                </p>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Also Add To Special List</label>
              <select
                value={recurringFormData.alsoAddToSpecialList}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, alsoAddToSpecialList: e.target.value })}
                className="input-custom"
                style={{ width: '100%' }}
                aria-label="Also add to special list"
              >
                <option value="none">None</option>
                <option value="main">To Do Today</option>
                <option value="tomorrow">To Do Tomorrow</option>
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                Optionally also add tasks to a special list when they are created
              </p>
            </div>

            <div className="modal-footer" style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                className="btn-custom btn-secondary-custom"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-custom btn-primary-custom"
              >
                Create Recurring Task
              </button>
            </div>
          </form>
      </div>
    </div>
  );
}

